/**
 * Client Portal Login Page
 *
 * Page de connexion via Magic Link pour les clients (maîtres d'ouvrage)
 * Design System: Contexte LANDING (text-white, bg-teal gradient)
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, Loader2, CheckCircle, Building2 } from 'lucide-react';
import { clientPortalAuthService } from '../services/client-portal.service';
import { useClientPortalAuthStore } from '../stores/clientPortalAuth.store';

export function ClientPortalLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useClientPortalAuthStore();

  const [step, setStep] = useState<'email' | 'sent' | 'verifying' | 'error'>('email');
  const [email, setEmail] = useState('');
  const [companyId, setCompanyId] = useState(searchParams.get('company') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vérifier si on arrive avec un token de magic link
  const token = searchParams.get('token');

  // Si token présent, vérifier automatiquement
  useState(() => {
    if (token) {
      verifyMagicLink(token);
    }
  });

  const verifyMagicLink = async (magicToken: string) => {
    setStep('verifying');
    setError(null);

    try {
      const result = await clientPortalAuthService.verifyMagicLink(magicToken);

      if (result.success && result.user) {
        login(result.user);
        navigate('/portal/dashboard', { replace: true });
      } else {
        setError(result.error || t('common.clientPortal.login.invalidLink'));
        setStep('error');
      }
    } catch (err) {
      setError(t('common.clientPortal.login.verifyError'));
      setStep('error');
    }
  };

  const handleRequestMagicLink = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await clientPortalAuthService.requestMagicLink(email, companyId);

      if (result.success) {
        setStep('sent');
      } else {
        setError(result.message || t('common.clientPortal.login.sendError'));
      }
    } catch (err) {
      setError(t('common.clientPortal.login.serverError'));
    } finally {
      setIsLoading(false);
    }
  }, [email, companyId]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(212,255,0,0.1),transparent_50%)]" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo / Brand */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl mb-4">
              <Building2 className="w-8 h-8 text-lime-400" />
            </div>
            <h1 className="text-3xl font-bold text-white">{t('common.clientPortal.title')}</h1>
            <p className="text-white/80 mt-2">{t('common.clientPortal.followProjects')}</p>
          </div>

          {/* Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            {step === 'email' && (
              <form onSubmit={handleRequestMagicLink} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-white">{t('common.clientPortal.login.title')}</h2>
                  <p className="text-white/60 text-sm mt-1">
                    {t('common.clientPortal.login.subtitle')}
                  </p>
                </div>

                {error && (
                  <div className="p-4 bg-red-500/20 border border-red-400/30 rounded-xl">
                    <p className="text-red-200 text-sm">{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('common.clientPortal.login.emailLabel')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.login.placeholders.email')}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur-md
                          border border-white/20 rounded-xl
                          text-white placeholder-white/40
                          focus:ring-2 focus:ring-lime-400/50 focus:border-transparent
                          transition-all duration-300"
                      />
                    </div>
                  </div>

                  {!searchParams.get('company') && (
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        {t('common.clientPortal.login.companyCodeLabel')}
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          type="text"
                          value={companyId}
                          onChange={(e) => setCompanyId(e.target.value)}
                          placeholder={t('common.clientPortal.login.companyCodePlaceholder')}
                          required
                          className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur-md
                            border border-white/20 rounded-xl
                            text-white placeholder-white/40
                            focus:ring-2 focus:ring-lime-400/50 focus:border-transparent
                            transition-all duration-300"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email || !companyId}
                  className="w-full py-3.5 bg-gradient-to-r from-lime-400 to-lime-500
                    text-teal-800 font-bold rounded-xl
                    hover:shadow-lg hover:shadow-lime-400/25 hover:-translate-y-0.5
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0
                    transition-all duration-300
                    flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span>{t('common.clientPortal.login.receiveLink')}</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}

            {step === 'sent' && (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-lime-400/20 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-lime-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">{t('common.clientPortal.login.emailSent')}</h2>
                <p className="text-white/70">
                  {t('common.clientPortal.login.checkInbox')} <strong className="text-white">{email}</strong>
                </p>
                <p className="text-white/50 text-sm mt-4">
                  {t('common.clientPortal.login.linkExpires')}
                </p>
                <button
                  onClick={() => setStep('email')}
                  className="mt-6 text-lime-400 hover:text-lime-300 text-sm font-medium transition-colors"
                >
                  {t('common.clientPortal.login.resendLink')}
                </button>
              </div>
            )}

            {step === 'verifying' && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-lime-400 animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white">{t('common.clientPortal.login.verifying')}</h2>
              </div>
            )}

            {step === 'error' && (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
                  <Mail className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">{t('common.clientPortal.login.invalidLinkTitle')}</h2>
                <p className="text-white/70">{error}</p>
                <button
                  onClick={() => {
                    setStep('email');
                    setError(null);
                  }}
                  className="mt-6 w-full py-3 bg-white/10 hover:bg-white/20
                    text-white font-medium rounded-xl
                    transition-all duration-300"
                >
                  {t('common.clientPortal.login.requestNewLink')}
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-white/50 text-sm mt-6">
            {t('common.clientPortal.login.needHelp')}
          </p>
        </div>
      </div>
    </div>
  );
}
